(() => {
  if (window.__dommaSubscriptionSelectorLoaded) return;
  window.__dommaSubscriptionSelectorLoaded = true;

  class SubscriptionSelector {
    constructor() {
      this.init();
      this.setupGlobalListener();
    }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
    
    // Escucha evento del drawer para re-inicializar
    document.addEventListener('subscription:reinit', () => {
      this.setupEventListeners();
    });
  }

  setupGlobalListener() {
    // Listener global para actualizar el CTA cuando cambia la selección de suscripción
    document.addEventListener('subscription:selectionChanged', (event) => {
      this.updateCTAButton(event.detail);
    });
  }

  setupEventListeners() {
    const selectors = document.querySelectorAll('.subscription-selector-native');
    selectors.forEach(selector => this.setupSelectorListeners(selector));
  }

  setupSelectorListeners(selector) {
    if (selector.dataset.subscriptionInitialized === 'true') {
      return;
    }
    selector.dataset.subscriptionInitialized = 'true';

    const radioInputs = selector.querySelectorAll('input[type="radio"]');
    const sellingPlanInput = selector.querySelector('.selected-selling-plan-id');
    const variantInput = selector.querySelector('.selected-variant-id');
    
    radioInputs.forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.handleRadioChange(e, selector, sellingPlanInput, variantInput);
      });
    });

    this.bindFormSubmit(selector);

    this.applyUrlPreselect(selector);

    const initiallyChecked = selector.querySelector('input[type="radio"]:checked');
    if (initiallyChecked) {
      this.handleRadioChange({ target: initiallyChecked }, selector, sellingPlanInput, variantInput);
    } else {
      const firstEnabled = selector.querySelector('input[type="radio"]:not([disabled])');
      if (firstEnabled) {
        firstEnabled.checked = true;
        this.handleRadioChange({ target: firstEnabled }, selector, sellingPlanInput, variantInput);
      } else {
        this.updateHiddenInputs(selector, sellingPlanInput, variantInput);
      }
    }
  }

  // DOMMA 23-10-2026: preseleccion por URL — ?plan=trimestral marca el plan trimestral,
  // ?plan=unica (o ?plan=única) marca compra única. Sin parametro, todo sigue como antes.
  // Requiere data-plan-kind en los radios (snippets/kg-custom-subscription-block-updated.liquid).
  // Para DESACTIVAR: borrar este metodo y su llamada en setupSelectorListeners.
  applyUrlPreselect(selector) {
    let kind = null;
    try {
      const raw = (new URLSearchParams(window.location.search).get('plan') || '').toLowerCase();
      const map = { trimestral: 'quarterly', unica: 'onetime', 'única': 'onetime', bimensual: 'bimonthly', mensual: 'monthly' };
      kind = Object.prototype.hasOwnProperty.call(map, raw) ? map[raw] : null;
    } catch (e) {
      return;
    }
    if (!kind) return;

    const target = selector.querySelector('input[type="radio"][data-plan-kind="' + kind + '"]:not([disabled])');
    if (!target || target.checked) return;

    const current = selector.querySelector('input[type="radio"]:checked');
    if (current) current.checked = false;
    target.checked = true;
  }

  bindFormSubmit(selector) {
    const form = selector.closest('.wt-product')?.querySelector('form[data-type="add-to-cart-form"]') ||
                 document.querySelector('form[data-type="add-to-cart-form"]');

    if (!form) return;
    if (form.dataset.subscriptionSubmitBound === 'true') return;
    form.dataset.subscriptionSubmitBound = 'true';

    form.addEventListener('submit', () => {
      this.syncSelectionToForm(selector, form);
    }, true);
  }

  syncSelectionToForm(selector, form) {
    const checkedRadio = selector.querySelector('input[type="radio"]:checked');
    if (!checkedRadio || !form) return;

    const variantInput = form.querySelector('input[name="id"]');
    const sellingPlanInput = form.querySelector('input[name="selling_plan"]') ||
                           this.createSellingPlanInput(form);

    if (checkedRadio.dataset.radioType === 'one_time_purchase') {
      if (variantInput && checkedRadio.dataset.variantId) {
        variantInput.value = checkedRadio.dataset.variantId;
      }
      if (sellingPlanInput) sellingPlanInput.value = '';
    } else if (checkedRadio.dataset.radioType === 'selling_plan') {
      if (variantInput && checkedRadio.dataset.variantId) {
        variantInput.value = checkedRadio.dataset.variantId;
      }
      if (sellingPlanInput) sellingPlanInput.value = checkedRadio.dataset.sellingPlanId || '';
    }
  }

  handleRadioChange(event, selector, sellingPlanInput, variantInput) {
    const radio = event.target;
    
    this.updateHiddenInputs(selector, sellingPlanInput, variantInput);
    
    this.updateMainProductForm(radio);
    
    // Lanzar evento personalizado con la información de la selección
    this.dispatchSelectionEvent(radio);
    
    this.updateCTAFromRadio(radio);
  }

  updateCTAFromRadio(radio) {

    const form = radio.closest('.wt-product')?.querySelector('form[data-type="add-to-cart-form"]') ||
                 document.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!form) return;

    const ctaButton = form.querySelector('button[name="add"]');
    if (!ctaButton) return;

    const showPer =
      typeof radio.dataset.showPer !== 'undefined'
        ? radio.dataset.showPer === 'true'
        : radio.dataset.radioType === 'selling_plan';

    const detail = {
      variantPrice: radio.dataset.variantPrice || null,
      variantCompareAtPrice: radio.dataset.variantCompareAtPrice || null,
      isSubscription: radio.dataset.radioType === 'selling_plan',
      showPer
    };

    this.updateCTAPrices(ctaButton, detail);
  }

  updateHiddenInputs(selector, sellingPlanInput, variantInput) {
    const checkedRadio = selector.querySelector('input[type="radio"]:checked');
    
    if (!checkedRadio) return;
    
    const radioType = checkedRadio.dataset.radioType;
    
    if (radioType === 'one_time_purchase') {
      if (sellingPlanInput) sellingPlanInput.value = '';
      if (variantInput) variantInput.value = checkedRadio.dataset.variantId;
    } else if (radioType === 'selling_plan') {
      if (sellingPlanInput) sellingPlanInput.value = checkedRadio.dataset.sellingPlanId;
      if (variantInput) variantInput.value = checkedRadio.dataset.variantId;
    }
  }

  updateMainProductForm(radio) {
    let mainForm = radio.closest('.wt-product')?.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!mainForm) {
      mainForm = document.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    if (!mainForm) return;

    const variantInput = mainForm.querySelector('input[name="id"]');
    const sellingPlanInput = mainForm.querySelector('input[name="selling_plan"]') || 
                           this.createSellingPlanInput(mainForm);

    if (radio.dataset.radioType === 'one_time_purchase') {
      if (variantInput) variantInput.value = radio.dataset.variantId;
      if (sellingPlanInput) sellingPlanInput.value = '';
    } else if (radio.dataset.radioType === 'selling_plan') {
      if (variantInput) variantInput.value = radio.dataset.variantId;
      if (sellingPlanInput) sellingPlanInput.value = radio.dataset.sellingPlanId;
    }

    if (variantInput) {
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  createSellingPlanInput(form) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.value = '';
    form.appendChild(input);
    return input;
  }

  dispatchSelectionEvent(radio) {
    const showPer =
      typeof radio.dataset.showPer !== 'undefined'
        ? radio.dataset.showPer === 'true'
        : radio.dataset.radioType === 'selling_plan';

    const eventData = {
      radioType: radio.dataset.radioType,
      variantId: radio.dataset.variantId,
      sellingPlanId: radio.dataset.sellingPlanId || null,
      isSubscription: radio.dataset.radioType === 'selling_plan',
      isOneTimePurchase: radio.dataset.radioType === 'one_time_purchase',
      variantPrice: radio.dataset.variantPrice || null,
      variantCompareAtPrice: radio.dataset.variantCompareAtPrice || null,
      showPer,
      element: radio
    };

    // Lanzar evento en el documento para que pueda ser escuchado globalmente
    const customEvent = new CustomEvent('subscription:selectionChanged', {
      detail: eventData,
      bubbles: true,
      cancelable: false
    });
    
    document.dispatchEvent(customEvent);
    
  }

  updateCTAButton(detail) {
    if (!detail) return;

    const form = detail.element.closest('.wt-product')?.querySelector('form[data-type="add-to-cart-form"]') ||
                 document.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!form) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput && detail.variantId) {
      variantInput.value = detail.variantId;
    }

    let sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      form.appendChild(sellingPlanInput);
    }
    sellingPlanInput.value = detail.sellingPlanId || '';

    const ctaButton = form.querySelector('button[name="add"]');
    if (ctaButton) {
      this.updateCTAPrices(ctaButton, detail);
    }
  }

  updateCTAPrices(button, detail) {
    const buttons = [
      button,
      ...document.querySelectorAll('sticky-buy-button.kg-wt-product__sticky')
    ];

    buttons.forEach((btn) => {
      const priceElements = btn.querySelectorAll('[data-price]');
      const compareElements = btn.querySelectorAll('[data-compare]');
      const perElements = btn.querySelectorAll('[data-per]');

      // Price
      priceElements.forEach((priceElement) => {
        if (detail.variantPrice) {
          priceElement.textContent = detail.variantPrice;
        }
      });

      // Per
      const shouldShowPer =
        typeof detail.showPer === 'boolean'
          ? detail.showPer
          : detail.isSubscription;

      perElements.forEach((perElement) => {
        perElement.style.display = shouldShowPer ? '' : 'none';
      });

      // Compare
      compareElements.forEach((compareElement) => {
        if (
          detail.variantCompareAtPrice &&
          detail.variantCompareAtPrice !== detail.variantPrice
        ) {
          compareElement.textContent = detail.variantCompareAtPrice;
          compareElement.style.display = '';
        } else {
          compareElement.style.display = 'none';
        }
      });
    });
  }
  // updateCTAPrices(button, detail) {
  //   const priceElement = button.querySelector('[data-price]');
  //   const compareElement = button.querySelector('[data-compare]');
  //   const perElement = button.querySelector('[data-per]');

  //   if (!priceElement) return;

  //   if (detail.variantPrice) {
  //     priceElement.textContent = detail.variantPrice;
  //   }

  //   if (perElement) {
  //     const shouldShowPer = typeof detail.showPer === 'boolean' ? detail.showPer : detail.isSubscription;
  //     perElement.style.display = shouldShowPer ? '' : 'none';
  //   }

  //   if (compareElement) {
  //     if (detail.isSubscription && detail.variantCompareAtPrice && detail.variantCompareAtPrice !== detail.variantPrice) {
  //       compareElement.textContent = detail.variantCompareAtPrice;
  //       compareElement.style.display = '';
  //     } else if (!detail.isSubscription && detail.variantCompareAtPrice && detail.variantCompareAtPrice !== detail.variantPrice) {
  //       compareElement.textContent = detail.variantCompareAtPrice;
  //       compareElement.style.display = '';
  //     } else {
  //       compareElement.style.display = 'none';
  //     }
  //   }
  // }
  }

  new SubscriptionSelector();
})();